// tabs/EditTab.tsx
import { useEffect, useState } from 'react';
import {
  Stack, Group, Button, Textarea, Switch, Select, Divider, Loader,
} from '@mantine/core';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService, useACCourseService,
  Registration, UpdateRegistrationRequest, CourseDateModelView,
} from '@actschurch/ccms-sdk';

// ─── Props ────────────────────────────────────────────────────────────────────
interface EditTabProps {
  registration: Registration;
  onSaved: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const dateLabelParts = (d: CourseDateModelView): string => {
  const parts: string[] = [];
  if (d.periodDisplay ?? d.startDisplay) parts.push(d.periodDisplay ?? d.startDisplay ?? '');
  if (d.frequency)                        parts.push(d.frequency);
  const mode = d.modeDisplay ?? (d.isRemote ? 'Remote' : d.locationName);
  if (mode) parts.push(mode);
  return parts.filter(Boolean).join('  ·  ');
};

// ─── EditTab ──────────────────────────────────────────────────────────────────
export default function EditTab({ registration, onSaved }: EditTabProps) {
  const registrationService = useRegistrationService();
  const courseService       = useACCourseService();

  const [courseDates, setCourseDates] = useState<CourseDateModelView[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);

  const [dateID,       setDateID]       = useState<string | null>(
    registration.dateID ? String(registration.dateID) : null
  );
  const [description,  setDescription]  = useState(registration.description ?? '');
  const [isActive,     setIsActive]     = useState(registration.isActive ?? true);

  const [saving, setSaving] = useState(false);

  // ── Fetch dates for this registration's course ────────────────────────────
  useEffect(() => {
    if (!registration.courseID) return;
    setLoadingDates(true);
    courseService.getDates(registration.courseID)
      .then(setCourseDates)
      .catch(() => toast.error('Error', 'Could not load course dates.'))
      .finally(() => setLoadingDates(false));
  }, [registration.courseID, courseService]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const payload: UpdateRegistrationRequest = {
      dateID:      dateID ? Number(dateID) : undefined,
      description: description || undefined,
      isActive,
    };

    setSaving(true);
    try {
      await registrationService.update(registration.registrationID, payload);
      onSaved();
      toast.success('Updated', 'Registration updated successfully.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to update registration.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack gap="md">

      {/* Intake date */}
      <Select
        label="Intake Date"
        placeholder={loadingDates ? 'Loading dates…' : courseDates.length === 0 ? 'No dates available' : 'Select an intake date'}
        data={courseDates.map((d) => ({ value: String(d.dateID), label: dateLabelParts(d) }))}
        value={dateID}
        onChange={setDateID}
        disabled={loadingDates || courseDates.length === 0}
        searchable
        rightSection={loadingDates ? <Loader size={14} /> : undefined}
      />

      {/* Description */}
      <Textarea
        label="Description"
        placeholder="Registration description"
        value={description}
        onChange={(e) => { const v = e.currentTarget.value; setDescription(v); }}
        minRows={3}
      />

      {/* Active toggle */}
      <Switch
        label="Active"
        checked={isActive}
        onChange={(e) => setIsActive(e.currentTarget.checked)}
      />

      <Divider my="sm" />

      <Group justify="flex-end">
        <Button loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}